import cn from 'classnames';
import { ReactNode, useCallback, useMemo } from 'react';

import type { BaseRowData } from '@adv-frontend/data-provider';
import { HStack } from '@direct-frontend/components';

import { VirtualItem } from '@tanstack/react-virtual';
import type { GridCell, GridRow, GridTable } from '../../../types';

import styles from '../Grid.module.css';
import { LoadMoreCell } from '../LoadMoreCell/LoadMoreCell';

/**
 * Пропсы компонента `LoadMoreRowCells`.
 *
 * @typeParam RowData Данные грида.
 */
export interface LoadMoreRowCellsProps<RowData extends BaseRowData> {
    /**
     * Дополнительные классы для строки.
     */
    classNames?: string;
    /**
     * Инстанс грида.
     */
    grid: GridTable<RowData>;
    /**
     * Инстанс строки.
     */
    row: GridRow<RowData>;
    /**
     * Функция для отрисовки содержимого ячейки.
     */
    renderCellContent: (props: { cell: GridCell<RowData> }) => ReactNode;
    /**
     * Фиксированная высота строки.
     */
    strictHeight?: number;
    /**
     * Виртуальные столбцы.
     */
    virtualColumns: VirtualItem[];
    /**
     * Скрывать ли нижний бордер у строки
     */
    hideBottomBorder: boolean;
}

/**
 * Компонент строки "загрузить еще" для грида.
 *
 * @typeParam RowData Данные грида.
 **/
export function LoadMoreRowCells<RowData extends BaseRowData>({
    row,
    grid,
    renderCellContent,
    strictHeight,
    virtualColumns,
    hideBottomBorder,
    classNames,
}: LoadMoreRowCellsProps<RowData>) {
    // Если высота строки фиксирована, то используем виртуализацию столбцов
    const centerVisibleCells = row.getCenterVisibleCells();
    const centerVirtualCells: GridCell<RowData>[] = useMemo(() => {
        if (strictHeight) {
            return virtualColumns.map(virtualColumn => {
                // Ячейка точно существует, так как виртуализация использует те же самые ячейки
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- см. выше
                const cell = centerVisibleCells[virtualColumn.index]!;
                return {
                    ...cell,
                    offsetLeft: virtualColumn.start,
                };
            });
        }

        return centerVisibleCells;
    }, [centerVisibleCells, strictHeight, virtualColumns]);

    const renderCells = useCallback(
        (cells: GridCell<RowData>[]) => {
            return (
                <HStack className={classNames}>
                    {cells.map(cell => {
                        return (
                            <LoadMoreCell key={cell.id} cell={cell}>
                                {renderCellContent({ cell })}
                            </LoadMoreCell>
                        );
                    })}
                </HStack>
            );
        },
        [classNames, renderCellContent]
    );

    // Определяем, какая группа ячеек является первой (для размещения кнопки дозагрузки)
    const hasLeftPinnedColumns = grid.getIsSomeColumnsPinned('left');
    const hasCenterColumns = Boolean(centerVisibleCells.length);

    return (
        <>
            {hasLeftPinnedColumns && (
                <HStack className={styles.pinnedLeftColumns}>{renderCells(row.getLeftVisibleCells())}</HStack>
            )}
            {hasCenterColumns && <HStack className={styles.centerColumns}>{renderCells(centerVirtualCells)}</HStack>}
            {/* Ставим фейковые строки в случае если нет центральных колонок, для того что бы появились строки по всей ширине грида */}
            {!hasCenterColumns && (
                <HStack className={cn(styles.rowHightlighter, hideBottomBorder ? null : styles.withBorder)} />
            )}
            {grid.getIsSomeColumnsPinned('right') && (
                <HStack className={styles.pinnedRightColumns}>{renderCells(row.getRightVisibleCells())}</HStack>
            )}
        </>
    );
}
